import DownloadIcon from "@mui/icons-material/Download";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";

import CustomerContractAcceptanceReviewApi from "../../../../../infrastructure/api/customerContractAcceptanceReview";
import ApplicantContractApi from "../../../../../infrastructure/api/applicantContract";
import ProjectWorkflowFoundationApi from "../../../../../infrastructure/api/projectWorkflowFoundation";
import {
  CUSTOMER_OFFER_STATUS_CODES,
  normalizeCustomerPriceOfferStatus,
} from "../../../../../infrastructure/utils/pricingQuoteStatus";
import { resolveUploadUrl } from "../../../../../infrastructure/utils/resolveUploadUrl";

const REVIEW_UPLOADED = 2;
const REVIEW_APPROVED = 3;

const normalizeReviewStatus = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  const n = Number(value);
  if (!Number.isNaN(n) && String(value).trim() !== "") return n;
  const map = {
    PendingCustomerUpload: 1,
    UploadedByCustomer: 2,
    ApprovedBySolarent: 3,
    RejectedBySolarent: 4,
  };
  return map[value] ?? null;
};

const normalizeContractAcceptance = (raw) => {
  if (!raw) return null;
  const reviewStatus = normalizeReviewStatus(raw?.reviewStatus ?? raw?.ReviewStatus);
  return {
    id: raw?.id ?? raw?.Id,
    reviewStatus,
    reviewStatusTitle: raw?.reviewStatusTitle ?? raw?.ReviewStatusTitle ?? "",
    uploadedSignedContractUrl:
      raw?.uploadedSignedContractUrl ?? raw?.UploadedSignedContractUrl ?? "",
    uploadedAt: raw?.uploadedAt ?? raw?.UploadedAt,
    canOperatorReview: raw?.canOperatorReview ?? raw?.CanOperatorReview ?? false,
  };
};

const normalizeEquipmentSnapshot = (raw) => {
  if (!raw) return null;
  return {
    requestId: raw?.requestId ?? raw?.RequestId,
    customerPriceOfferId: raw?.customerPriceOfferId ?? raw?.CustomerPriceOfferId,
    offerStatus: normalizeCustomerPriceOfferStatus(raw?.offerStatus ?? raw?.OfferStatus),
    offerStatusTitle: raw?.offerStatusTitle ?? raw?.OfferStatusTitle ?? "",
    equipmentSnapshotId: raw?.equipmentSnapshotId ?? raw?.EquipmentSnapshotId,
    hasMatchingSnapshot: raw?.hasMatchingSnapshot ?? raw?.HasMatchingSnapshot ?? false,
    canEnsureFromApprovedPrice:
      raw?.canEnsureFromApprovedPrice ?? raw?.CanEnsureFromApprovedPrice ?? false,
    isManualRecoveryAvailable:
      raw?.isManualRecoveryAvailable ?? raw?.IsManualRecoveryAvailable ?? false,
    eligibleEquipmentLineCount:
      raw?.eligibleEquipmentLineCount ?? raw?.EligibleEquipmentLineCount ?? 0,
    sourceStatus: raw?.sourceStatus ?? raw?.SourceStatus ?? "",
    blockReason: raw?.blockReason ?? raw?.BlockReason ?? "",
  };
};

const normalizeProjectContractSummary = (raw) => {
  if (!raw) return null;
  return {
    projectContractId: raw?.projectContractId ?? raw?.ProjectContractId,
    contractAmount: raw?.contractAmount ?? raw?.ContractAmount,
    canGenerateFromCustomerOffer:
      raw?.canGenerateFromCustomerOffer ?? raw?.CanGenerateFromCustomerOffer,
    generateBlockReason: raw?.generateBlockReason ?? raw?.GenerateBlockReason ?? "",
    equipmentSnapshot: normalizeEquipmentSnapshot(
      raw?.equipmentSnapshot ?? raw?.EquipmentSnapshot
    ),
  };
};

const normalizeApplicantContract = (raw) => {
  if (!raw) return null;
  return {
    contractId: raw?.contractId ?? raw?.ContractId,
    contractNumber: raw?.contractNumber ?? raw?.ContractNumber ?? "",
    projectCode: raw?.projectCode ?? raw?.ProjectCode ?? "",
    status: raw?.status ?? raw?.Status,
    statusTitle: raw?.statusTitle ?? raw?.StatusTitle ?? "",
    versionNumber: raw?.versionNumber ?? raw?.VersionNumber,
    pdfSha256: raw?.pdfSha256 ?? raw?.PdfSha256 ?? "",
    verificationCode: raw?.verificationCode ?? raw?.VerificationCode ?? "",
    pdfDownloadUrl: raw?.pdfDownloadUrl ?? raw?.PdfDownloadUrl ?? "",
    createOn: raw?.createOn ?? raw?.CreateOn,
  };
};

const isOperatorSource = (source) =>
  source === "Management" || source === "SolarentOperator";

const formatMoney = (v) => {
  const num = Number(v);
  if (!Number.isFinite(num) || num <= 0) return null;
  return `${num.toLocaleString("fa-IR")} ریال`;
};

const resolveEmptyContractAcceptanceMessage = (projectContractSummary) => {
  const snapshot = projectContractSummary?.equipmentSnapshot;
  const offerStatus = snapshot?.offerStatus;

  if (!snapshot?.customerPriceOfferId) {
    return (
      snapshot?.blockReason ||
      "پیشنهاد قیمت مشتری برای این درخواست یافت نشد. ابتدا از بخش قیمت‌گذاری اقدام کنید."
    );
  }

  if (
    offerStatus === CUSTOMER_OFFER_STATUS_CODES.Draft ||
    offerStatus === CUSTOMER_OFFER_STATUS_CODES.PendingApproval ||
    offerStatus === CUSTOMER_OFFER_STATUS_CODES.RejectedBySolarent
  ) {
    const statusLabel = snapshot?.offerStatusTitle
      ? ` (وضعیت فعلی: ${snapshot.offerStatusTitle})`
      : "";
    return `قیمت نهایی هنوز برای تأیید مشتری ارسال نشده است${statusLabel}. از بخش قیمت‌گذاری، قیمت را محاسبه و برای مشتری ارسال کنید.`;
  }

  if (offerStatus === CUSTOMER_OFFER_STATUS_CODES.ApprovedBySolarent ||
      offerStatus === CUSTOMER_OFFER_STATUS_CODES.AcceptedByCustomer) {
    return "قیمت نهایی تایید شده است. در صورت نبودن رکورد قرارداد، صفحه را بروزرسانی کنید یا قرارداد را از همین بخش تولید کنید.";
  }

  if (offerStatus === CUSTOMER_OFFER_STATUS_CODES.RejectedByCustomer) {
    return "مشتری درخواست بررسی مجدد قیمت داده است. پس از اصلاح و ارسال مجدد قیمت، فرایند قرارداد ادامه می‌یابد.";
  }

  return (
    snapshot?.blockReason ||
    projectContractSummary?.generateBlockReason ||
    "پس از تایید قیمت توسط کارشناس سولارنت، قرارداد متقاضی در این بخش آماده مشاهده و امضا می‌شود."
  );
};

const WorkspaceCustomerContractPanel = ({ requestId, source, onChanged }) => {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [contractAcceptance, setContractAcceptance] = useState(null);
  const [projectContractSummary, setProjectContractSummary] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [ensureSnapshotOpen, setEnsureSnapshotOpen] = useState(false);
  const [applicantContract, setApplicantContract] = useState(null);

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError("");
    try {
      const requests = [
        CustomerContractAcceptanceReviewApi.getByRequest(source, requestId),
        ProjectWorkflowFoundationApi.getProjectContractWorkspaceSummary(source, requestId),
      ];
      if (isOperatorSource(source)) {
        requests.push(ApplicantContractApi.getByRequest(requestId));
      }

      const [contractRes, projectContractRes, applicantContractRes] = await Promise.all(requests);

      if (contractRes.ok && contractRes.data) {
        setContractAcceptance(normalizeContractAcceptance(contractRes.data));
      } else {
        setContractAcceptance(null);
      }

      if (projectContractRes.ok && projectContractRes.data) {
        setProjectContractSummary(normalizeProjectContractSummary(projectContractRes.data));
      } else {
        setProjectContractSummary(null);
      }

      if (isOperatorSource(source)) {
        if (applicantContractRes?.ok && applicantContractRes.data) {
          setApplicantContract(normalizeApplicantContract(applicantContractRes.data));
        } else {
          setApplicantContract(null);
        }
      }
    } catch {
      setError("خطا در بارگذاری وضعیت قرارداد مشتری.");
    } finally {
      setLoading(false);
    }
  }, [requestId, source]);

  useEffect(() => {
    load();
  }, [load]);

  const handleReview = async (approve) => {
    if (!contractAcceptance?.id) return;
    if (!approve && !rejectReason.trim()) {
      setError("دلیل رد الزامی است.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await CustomerContractAcceptanceReviewApi.reviewSignedCustomerContract(
        source,
        {
          customerContractAcceptanceId: contractAcceptance.id,
          approve,
          reason: approve ? undefined : rejectReason.trim(),
        }
      );
      if (res.ok) {
        setSuccess(res.message || (approve ? "قرارداد تایید شد." : "قرارداد رد شد."));
        setRejectOpen(false);
        setRejectReason("");
        await load();
        onChanged?.();
      } else {
        setError(res.message || "خطا در بررسی قرارداد.");
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleEnsureEquipmentSnapshot = async () => {
    setActionLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await ProjectWorkflowFoundationApi.ensureEquipmentSnapshotFromApprovedPrice(
        source,
        { requestId, confirm: true }
      );
      if (res.ok) {
        setSuccess(res.message || "اسنپ‌شات تجهیزات ثبت شد.");
        setEnsureSnapshotOpen(false);
        await load();
        onChanged?.();
      } else {
        setError(res.message || "خطا در ثبت اسنپ‌شات تجهیزات.");
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateApplicantContract = async () => {
    setActionLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await ApplicantContractApi.generate(requestId, {
        refreshEquipmentSnapshotFromLatestPackage: true,
      });
      if (res.ok) {
        setSuccess(res.message || "قرارداد متقاضی تولید شد.");
        setApplicantContract(normalizeApplicantContract(res.data));
        await load();
        onChanged?.();
      } else {
        setError(res.message || "خطا در تولید قرارداد متقاضی.");
        // PDF may have been saved before a network drop — refresh to pick up a new version.
        try {
          await load();
        } catch {
          /* ignore */
        }
      }
    } catch (e) {
      setError(e?.message || "خطا در تولید قرارداد متقاضی.");
      try {
        await load();
      } catch {
        /* ignore */
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadApplicantPdf = async () => {
    if (!applicantContract?.contractId) return;
    setActionLoading(true);
    setError("");
    try {
      const blob = await ApplicantContractApi.downloadPdf(applicantContract.contractId);
      const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `applicant-contract-${applicantContract.contractNumber || applicantContract.contractId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("خطا در دانلود PDF قرارداد متقاضی.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateProjectContract = async () => {
    setActionLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await ProjectWorkflowFoundationApi.generateProjectContractFromCustomerOffer(
        source,
        { requestId }
      );
      if (res.ok) {
        setSuccess(res.message || "قرارداد اجرا ایجاد شد.");
        await load();
        onChanged?.();
      } else {
        setError(res.message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const uploadedFileUrl = contractAcceptance?.uploadedSignedContractUrl
    ? resolveUploadUrl(contractAcceptance.uploadedSignedContractUrl)
    : "";

  const needsReview =
    contractAcceptance?.canOperatorReview === true ||
    contractAcceptance?.reviewStatus === REVIEW_UPLOADED;

  const isApproved = contractAcceptance?.reviewStatus === REVIEW_APPROVED;

  const equipmentSnapshot = projectContractSummary?.equipmentSnapshot;
  const showEquipmentSnapshotBlock =
    isApproved &&
    !projectContractSummary?.projectContractId &&
    equipmentSnapshot &&
    !equipmentSnapshot.hasMatchingSnapshot;
  const isSourceValid = (equipmentSnapshot?.eligibleEquipmentLineCount ?? 0) > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 1.5,
        borderColor: needsReview ? "warning.main" : undefined,
        bgcolor: needsReview ? "warning.50" : undefined,
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TaskAltIcon color={needsReview ? "warning" : "primary"} fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>
            قرارداد مشتری-سولارنت
          </Typography>
          {loading ? <CircularProgress size={18} /> : null}
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
            {error}
          </Alert>
        ) : null}
        {success ? <Alert severity="success">{success}</Alert> : null}

        {!loading && !contractAcceptance ? (
          <Alert severity="info">
            {resolveEmptyContractAcceptanceMessage(projectContractSummary)}
          </Alert>
        ) : null}

        {!loading && contractAcceptance ? (
          <>
            <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
              <Chip
                size="small"
                color={isApproved ? "success" : needsReview ? "warning" : "default"}
                label={`وضعیت: ${contractAcceptance.reviewStatusTitle || "—"}`}
              />
              {contractAcceptance.uploadedSignedContractUrl ? (
                <Chip size="small" variant="outlined" color="info" label="فایل آپلود شده" />
              ) : null}
            </Stack>

            {needsReview ? (
              <Alert severity="warning">
                مشتری قرارداد امضا‌شده را بارگذاری کرده است. لطفاً فایل را بررسی کرده و
                تایید یا رد کنید تا پرونده به مرحله بعد برود.
              </Alert>
            ) : null}

            {uploadedFileUrl ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                href={uploadedFileUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                مشاهده قرارداد امضا‌شده
              </Button>
            ) : null}

            {needsReview ? (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <Button
                  variant="contained"
                  color="success"
                  disabled={actionLoading}
                  onClick={() => handleReview(true)}
                >
                  تایید قرارداد امضا‌شده
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={actionLoading}
                  onClick={() => {
                    setRejectOpen(true);
                    setRejectReason("");
                  }}
                >
                  رد قرارداد
                </Button>
              </Stack>
            ) : null}

            {isApproved && !projectContractSummary?.projectContractId ? (
              <Stack spacing={1}>
                {showEquipmentSnapshotBlock ? (
                  <Alert severity="warning">
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      اسنپ‌شات تجهیزات ثبت نشده است
                    </Typography>
                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        وضعیت منبع: {equipmentSnapshot.sourceStatus || "—"}
                      </Typography>
                      <Typography variant="body2">
                        تعداد اقلام تجهیزات: {equipmentSnapshot.eligibleEquipmentLineCount ?? 0}
                      </Typography>
                      <Typography variant="body2">
                        منبع معتبر: {isSourceValid ? "بله" : "خیر"}
                      </Typography>
                      {equipmentSnapshot.blockReason ? (
                        <Typography variant="caption" color="text.secondary">
                          {equipmentSnapshot.blockReason}
                        </Typography>
                      ) : null}
                    </Stack>
                    {equipmentSnapshot.isManualRecoveryAvailable ? (
                      <Button
                        sx={{ mt: 1, alignSelf: "flex-start" }}
                        variant="contained"
                        color="warning"
                        disabled={actionLoading}
                        onClick={() => setEnsureSnapshotOpen(true)}
                      >
                        ثبت اسنپ‌شات تجهیزات از قیمت تأییدشده
                      </Button>
                    ) : null}
                  </Alert>
                ) : null}

                {!showEquipmentSnapshotBlock ? (
                  <Alert severity="info">
                    قرارداد مشتری تایید شد. برای ادامه، قرارداد اجرای پروژه را ایجاد کنید.
                  </Alert>
                ) : null}
                {projectContractSummary?.canGenerateFromCustomerOffer ? (
                  <Button
                    variant="contained"
                    disabled={actionLoading}
                    onClick={handleGenerateProjectContract}
                  >
                    ایجاد قرارداد اجرا
                  </Button>
                ) : projectContractSummary?.generateBlockReason && !showEquipmentSnapshotBlock ? (
                  <Typography variant="caption" color="text.secondary">
                    {projectContractSummary.generateBlockReason}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}

            {projectContractSummary?.projectContractId ? (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={
                  formatMoney(projectContractSummary.contractAmount)
                    ? `قرارداد اجرا #${projectContractSummary.projectContractId} — ${formatMoney(
                        projectContractSummary.contractAmount
                      )}`
                    : `قرارداد اجرا #${projectContractSummary.projectContractId} — مبلغ نهایی ثبت نشده`
                }
              />
            ) : null}
          </>
        ) : null}

        {isOperatorSource(source) && !loading ? (
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "grey.50" }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight={700}>
                قرارداد متقاضی (PDF)
              </Typography>
              {applicantContract ? (
                <>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    <Chip
                      size="small"
                      label={`وضعیت: ${applicantContract.statusTitle || "—"}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`شماره: ${applicantContract.contractNumber || "—"}`}
                    />
                    {applicantContract.versionNumber ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`نسخه ${applicantContract.versionNumber}`}
                      />
                    ) : null}
                  </Stack>
                  {applicantContract.pdfSha256 ? (
                    <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                      SHA-256: {applicantContract.pdfSha256}
                    </Typography>
                  ) : null}
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      disabled={actionLoading}
                      onClick={handleDownloadApplicantPdf}
                    >
                      دانلود PDF قرارداد متقاضی
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={actionLoading}
                      onClick={handleGenerateApplicantContract}
                    >
                      تولید نسخه جدید
                    </Button>
                  </Stack>
                </>
              ) : (
                <Button
                  variant="contained"
                  disabled={actionLoading}
                  onClick={handleGenerateApplicantContract}
                >
                  تولید قرارداد متقاضی
                </Button>
              )}
            </Stack>
          </Paper>
        ) : null}
      </Stack>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>رد قرارداد امضا‌شده</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="دلیل رد"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>انصراف</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectReason.trim() || actionLoading}
            onClick={() => handleReview(false)}
          >
            ثبت رد
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={ensureSnapshotOpen}
        onClose={() => setEnsureSnapshotOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>ثبت اسنپ‌شات تجهیزات از قیمت تأییدشده</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Typography variant="body2">
              منبع: {equipmentSnapshot?.sourceStatus || "—"}
            </Typography>
            <Typography variant="body2">
              تعداد اقلام: {equipmentSnapshot?.eligibleEquipmentLineCount ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              با تایید، یک اسنپ‌شات غیرقابل‌تغییر از قیمت تأییدشده ثبت می‌شود.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnsureSnapshotOpen(false)}>انصراف</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={actionLoading}
            onClick={handleEnsureEquipmentSnapshot}
          >
            تایید و ثبت
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

WorkspaceCustomerContractPanel.propTypes = {
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  source: PropTypes.string.isRequired,
  onChanged: PropTypes.func,
};

export default WorkspaceCustomerContractPanel;
